import React, { useEffect, useState } from 'react';
import { useT } from '../../context/TenantContext';
import BackDeskLayout from '../../components/layout/BackDeskLayout';
import { Button } from '../../components/ui';

const PRIO_INFO = {
  critical: { label:'Crítica', color:'#B91C1C', bg:'#FEF2F2', icon:'🔴' },
  high:     { label:'Alta',    color:'#B45309', bg:'#FFFBEB', icon:'🟠' },
  medium:   { label:'Media',   color:'#0085CA', bg:'#E6F4FB', icon:'🔵' },
  low:      { label:'Baja',    color:'#15803D', bg:'#F0FDF4', icon:'🟢' },
};

function fmt(min) {
  if (min < 60)  return `${min} min`;
  if (min < 1440) return `${Math.round(min/60*10)/10} h`;
  return `${Math.round(min/1440*10)/10} días`;
}

export default function SLAConfig() {
  const { t } = useT();
  const token = localStorage.getItem('hd_token');
  const h = { 'Content-Type':'application/json', Authorization:`Bearer ${token}` };

  const [config,   setConfig]   = useState([]);
  const [loadError, setLoadError] = useState('');
  const [load,     setLoad]     = useState(null);
  const [saving,   setSaving]   = useState(null);
  const [editing,  setEditing]  = useState(null); // priority being edited
  const [form,     setForm]     = useState({ response_min:'', resolve_min:'' });
  const [msg,      setMsg]      = useState('');

  useEffect(() => {
    Promise.all([
      fetch('/api/sla/config', { headers:h }).then(r=>r.json()),
      fetch('/api/sla/estimated').then(r=>r.json()),
    ]).then(([cfg, est]) => {
      setConfig(Array.isArray(cfg) ? cfg : []);
      if (!Array.isArray(cfg)) setLoadError('Reinicia el backend para cargar la configuración SLA.');
      const loadMap = {};
      if (Array.isArray(est)) est.forEach(e => loadMap[e.priority] = e);
      setLoad(loadMap);
    }).catch(() => setLoadError('Error conectando con el backend.'));
  }, []);

  function startEdit(priority, row) {
    setEditing(priority);
    setForm({ response_min: row.response_min, resolve_min: row.resolve_min });
    setMsg('');
  }

  async function save(priority) {
    setSaving(priority);
    const res = await fetch(`/api/sla/config/${priority}`, {
      method:'PUT', headers:h,
      body: JSON.stringify({ response_min: Number(form.response_min), resolve_min: Number(form.resolve_min) })
    });
    const data = await res.json();
    setConfig(data);
    setEditing(null);
    setSaving(null);
    setMsg('✓ Configuración guardada');
    setTimeout(() => setMsg(''), 3000);
  }

  const inputStyle = {
    width:'100%', height:38, padding:'0 12px',
    border:'1.5px solid #E5E7EB', borderRadius:8,
    fontSize:13, color:'#111827', outline:'none',
    fontFamily:'var(--font-body)',
  };

  return (
    <BackDeskLayout title="Configuración de SLA">
      <div style={{maxWidth:860,margin:'0 auto'}}>

        {loadError && (
          <div style={{marginBottom:16,padding:'12px 16px',background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:10,fontSize:13,color:'#B45309'}}>
            ⚠ {loadError} Reinicia el backend con <code>npm start</code> y recarga la página.
          </div>
        )}
      {msg && (
          <div style={{marginBottom:16,padding:'10px 16px',background:'#F0FDF4',border:'1px solid #86EFAC',borderRadius:10,fontSize:13,color:'#15803D',fontWeight:600}}>
            {msg}
          </div>
        )}

        {/* Estado actual de carga */}
        {load && (
          <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:12,padding:'20px 24px',marginBottom:20,boxShadow:'0 1px 3px rgba(0,0,0,.05)'}}>
            <div style={{fontSize:13,fontWeight:700,marginBottom:14}}>Estado actual del sistema</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
              {['critical','high','medium','low'].map(p => {
                const l = load[p];
                const info = PRIO_INFO[p];
                if (!l) return null;
                const overloaded = l.load_factor > 1.5;
                return (
                  <div key={p} style={{padding:'14px 16px',background:info.bg,borderRadius:10,border:`1px solid ${info.color}30`}}>
                    <div style={{fontSize:12,fontWeight:700,color:info.color,marginBottom:8}}>{info.icon} {info.label}</div>
                    <div style={{fontSize:11,color:'#6B7280',marginBottom:4}}>Tiempo base</div>
                    <div style={{fontSize:18,fontWeight:700,color:'#111827',marginBottom:8}}>{fmt(l.base_min)}</div>
                    <div style={{fontSize:11,color:'#6B7280',marginBottom:4}}>Estimado ahora</div>
                    <div style={{fontSize:16,fontWeight:700,color: overloaded?'#B91C1C':info.color}}>
                      {fmt(l.estimated_min)}
                      {overloaded && <span style={{fontSize:10,marginLeft:4}}>⚠</span>}
                    </div>
                    <div style={{marginTop:8,fontSize:10,color:'#9CA3AF'}}>
                      Factor carga: ×{l.load_factor} · {l.open_tickets} tickets abiertos · {l.active_agents} agentes
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Configuración SLA */}
        <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:12,overflow:'hidden',boxShadow:'0 1px 3px rgba(0,0,0,.05)'}}>
          <div style={{padding:'14px 20px',borderBottom:'1px solid #F3F4F6',background:'#FAFAFA'}}>
            <div style={{fontSize:13,fontWeight:700}}>Tiempos de SLA por prioridad</div>
            <div style={{fontSize:12,color:'#9CA3AF',marginTop:2}}>El tiempo estimado real se calcula dinámicamente según la carga de tickets y agentes disponibles</div>
          </div>

          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead>
              <tr style={{background:'#FAFAFA'}}>
                {['Prioridad','Tiempo de respuesta','Tiempo de resolución','Acciones'].map(h=>(
                  <th key={h} style={{padding:'10px 20px',textAlign:'left',fontSize:10,fontWeight:600,letterSpacing:.4,textTransform:'uppercase',color:'#9CA3AF',borderBottom:'1px solid #F3F4F6'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(config) ? config : []).map(row => {
                const info = PRIO_INFO[row.priority];
                const isEditing = editing === row.priority;
                return (
                  <tr key={row.priority} style={{borderBottom:'1px solid #F9FAFB'}}>
                    <td style={{padding:'14px 20px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <span style={{fontSize:16}}>{info?.icon}</span>
                        <span style={{fontSize:13,fontWeight:700,color:info?.color}}>{info?.label}</span>
                      </div>
                    </td>
                    <td style={{padding:'14px 20px'}}>
                      {isEditing ? (
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <input style={{...inputStyle,width:90}} type="number" min="1" value={form.response_min}
                            onChange={e=>setForm(f=>({...f,response_min:e.target.value}))}/>
                          <span style={{fontSize:12,color:'#6B7280'}}>minutos</span>
                          <span style={{fontSize:11,color:'#9CA3AF'}}>({fmt(Number(form.response_min)||0)})</span>
                        </div>
                      ) : (
                        <div>
                          <span style={{fontSize:15,fontWeight:700,color:'#111827'}}>{fmt(row.response_min)}</span>
                          <span style={{fontSize:11,color:'#9CA3AF',marginLeft:8}}>{row.response_min} min</span>
                        </div>
                      )}
                    </td>
                    <td style={{padding:'14px 20px'}}>
                      {isEditing ? (
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <input style={{...inputStyle,width:90}} type="number" min="1" value={form.resolve_min}
                            onChange={e=>setForm(f=>({...f,resolve_min:e.target.value}))}/>
                          <span style={{fontSize:12,color:'#6B7280'}}>minutos</span>
                          <span style={{fontSize:11,color:'#9CA3AF'}}>({fmt(Number(form.resolve_min)||0)})</span>
                        </div>
                      ) : (
                        <div>
                          <span style={{fontSize:15,fontWeight:700,color:'#111827'}}>{fmt(row.resolve_min)}</span>
                          <span style={{fontSize:11,color:'#9CA3AF',marginLeft:8}}>{row.resolve_min} min</span>
                        </div>
                      )}
                    </td>
                    <td style={{padding:'14px 20px'}}>
                      {isEditing ? (
                        <div style={{display:'flex',gap:8}}>
                          <Button variant="primary" size="sm" style={{height:32}}
                            onClick={()=>save(row.priority)} disabled={saving===row.priority}>
                            {saving===row.priority ? 'Guardando…' : 'Guardar'}
                          </Button>
                          <Button variant="ghost" size="sm" style={{height:32}}
                            onClick={()=>setEditing(null)}>{t('general.cancel')}</Button>
                        </div>
                      ) : (
                        <Button variant="ghost" size="sm" style={{height:32}}
                          onClick={()=>startEdit(row.priority, row)}>Editar</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Beta notice */}
        <div style={{marginTop:16,padding:'14px 18px',background:'#FFFBEB',borderRadius:10,border:'1px solid #FDE68A',fontSize:12,color:'#92400E',lineHeight:1.7,display:'flex',gap:10}}>
          <span style={{fontSize:18,flexShrink:0}}>🧪</span>
          <div>
            <strong style={{color:'#78350F',display:'block',marginBottom:3}}>Función beta — Estimación de tiempos</strong>
            El cálculo del tiempo estimado es experimental y puede no ajustarse a la realidad. Se basa en un modelo simplificado
            que pondera los tickets abiertos por prioridad y los divide entre los agentes activos, aplicando un factor de carga
            de ×1.0 (sin carga) a ×3.0 (sistema saturado). Los resultados son orientativos y deben interpretarse con criterio.
          </div>
        </div>
      </div>
    </BackDeskLayout>
  );
}
